import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import okpdTree from '../assets/okpd2_tree.json';
import api from '../services/api';

const isLeaf = (node) => !node.children || node.children.length === 0;
const isFullOkpd = (code) => /^\d{2}\.\d{2}\.\d{2}\.\d{3}$/.test(code);

// Если у узла есть дети и код в полном формате DD.DD.DD.DDD — последний символ лишний
const fixCode = (node) => {
    if (!isLeaf(node) && isFullOkpd(node.code)) {
        return node.code.slice(0, -1);
    }
    return node.code;
};

const fixTree = (nodes) => nodes.map(node => ({
    ...node,
    code: fixCode(node),
    children: node.children ? fixTree(node.children) : undefined,
}));

const tree = fixTree(okpdTree);

const collectLeafCodes = (node, result = []) => {
    if (isLeaf(node)) result.push(node.code);
    if (node.children) node.children.forEach(c => collectLeafCodes(c, result));
    return result;
};

const TreeNode = ({ node, depth, onSelect, counts }) => {
    const [open, setOpen] = useState(false);
    const clickable = isLeaf(node);
    const count = counts?.[node.code];

    return (
        <div className="ot-node">
            <div
                className={`ot-row ot-depth-${Math.min(depth, 4)}${clickable ? ' ot-clickable' : ''}`}
                onClick={() => clickable ? onSelect(node.code) : setOpen(o => !o)}
            >
                <span className="ot-toggle">
                    {clickable
                        ? <span className="ot-leaf-icon" />
                        : <span className={`ot-chevron${open ? ' open' : ''}`} onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>›</span>
                    }
                </span>
                <span className="ot-code">{node.code}</span>
                <span className="ot-name">{node.name}</span>
                {clickable && count != null && (
                    <span className="ot-count-badge">{count.toLocaleString('ru-RU')} контр.</span>
                )}
                {clickable && <span className="ot-search-hint">Найти →</span>}
            </div>

            {open && node.children && (
                <div className="ot-children">
                    {node.children.map(child => (
                        <TreeNode key={child.code} node={child} depth={depth + 1} onSelect={onSelect} counts={counts} />
                    ))}
                </div>
            )}
        </div>
    );
};

const SectionCard = ({ node, onSelect }) => {
    const [open, setOpen] = useState(false);
    const [counts, setCounts] = useState(null);

    const handleOpen = async () => {
        const next = !open;
        setOpen(next);
        if (next && counts === null) {
            const codes = collectLeafCodes(node);
            if (codes.length > 0) {
                try {
                    const res = await api.post('/okpd-counts', codes);
                    setCounts(res.data);
                } catch {
                    setCounts({});
                }
            } else {
                setCounts({});
            }
        }
    };

    return (
        <div className={`ot-section${open ? ' open' : ''}`}>
            <div className="ot-section-header" onClick={handleOpen}>
                <div className="ot-section-letter">{node.code}</div>
                <div className="ot-section-info">
                    <div className="ot-section-name">{node.name}</div>
                    <div className="ot-section-count">{node.children?.length || 0} подразделов</div>
                </div>
                <span className={`ot-section-chevron${open ? ' open' : ''}`}>›</span>
            </div>
            {open && node.children && (
                <div className="ot-section-body">
                    {node.children.map(child => (
                        <TreeNode key={child.code} node={child} depth={1} onSelect={onSelect} counts={counts} />
                    ))}
                </div>
            )}
        </div>
    );
};

const OkpdTreePage = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');

    const handleSelect = (code) => navigate(`/?mode=okpd&q=${code}`);

    const searchResults = useMemo(() => {
        if (!search.trim() || search.length < 2) return null;
        const q = search.toLowerCase();
        const results = [];
        const walk = (nodes) => {
            for (const node of nodes) {
                if (node.name.toLowerCase().includes(q) || node.code.toLowerCase().includes(q)) {
                    results.push(node);
                }
                if (results.length >= 60) return;
                if (node.children) walk(node.children);
                if (results.length >= 60) return;
            }
        };
        walk(tree);
        return results;
    }, [search]);

    return (
        <div className="okpd-page">
            <div className="okpd-hero">
                <div className="okpd-hero-inner">
                    <h1 className="okpd-hero-title">Справочник ОКПД 2</h1>
                    <p className="okpd-hero-sub">Классификатор продукции по видам экономической деятельности. Выберите код — найдём контракты по КТРУ.</p>
                    <div className="okpd-hero-search">
                        <svg className="okpd-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input
                            className="okpd-hero-input"
                            type="text"
                            placeholder="Поиск по названию или коду ОКПД..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {search && (
                            <button className="okpd-search-clear" onClick={() => setSearch('')}>✕</button>
                        )}
                    </div>
                </div>
            </div>

            <div className="container">
                {searchResults ? (
                    <div className="ot-search-results">
                        {searchResults.length === 0 ? (
                            <div className="okpd-empty">Ничего не найдено по запросу «{search}»</div>
                        ) : (
                            <>
                                <div className="ot-results-count">{searchResults.length} результатов</div>
                                {searchResults.map(node => (
                                    <div
                                        key={node.code}
                                        className={`ot-result-row${isLeaf(node) ? ' ot-clickable' : ''}`}
                                        onClick={() => isLeaf(node) && handleSelect(node.code)}
                                    >
                                        <span className="ot-code">{node.code}</span>
                                        <span className="ot-name">{node.name}</span>
                                        {isLeaf(node) && (
                                            <span className="ot-search-hint">Найти по КТРУ →</span>
                                        )}
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                ) : (
                    <div className="ot-sections">
                        {tree.map(node => (
                            <SectionCard key={node.code} node={node} onSelect={handleSelect} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OkpdTreePage;
